import React, { useState, useEffect, useRef } from "react";
import { EditorState, RichUtils, ContentState, convertFromHTML } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import createCounterPlugin from "draft-js-counter-plugin";
import Editor from "draft-js-plugins-editor";
import punycode from "punycode";
import "draft-js/dist/Draft.css";
import "draft-js-counter-plugin/lib/plugin.css";
import "./draftStyles.css";
import linkPlugin from "./plugins/addLinkPlugin";
import { ReactComponent as LinkIcon } from "@svgr/webpack?-svgo,+titleProp,+ref!../Icon/Stand-Alone/Link.svg";
import { TextEditorBox, TextEditorToolbar } from "./components";
const addLinkPrompt = "Add Link";
const boldStyle = {
  fontWeight: "700"
};
const italicStyle = {
  fontFamily: "serif",
  fontStyle: "italic"
};
const inlineStyles = [{
  style: "BOLD",
  label: React.createElement("b", {
    style: boldStyle
  }, "B")
}, {
  style: "ITALIC",
  label: React.createElement("i", {
    style: italicStyle
  }, "I")
}, {
  style: "UNDERLINE",
  label: React.createElement("u", null, "U")
}];
const blockTypes = {
  h1: {
    blockName: "header-one",
    class: "headerOne",
    label: "H"
  }
}; // Parent class of draft.js Editor

export const TextEditor = ({
  html,
  getHtml,
  characterCountLimit
}) => {
  const transformHtmlInput = html => {
    const blocksFromHTML = convertFromHTML(html || "");
    const state = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
    return EditorState.createWithContent(state);
  }; // Use parent callback to send draft.js html content


  const sendHtml = () => {
    if (getHtml) {
      getHtml(stateToHTML(editorState.getCurrentContent()));
    }
  };

  const [editorState, setEditorState] = useState(transformHtmlInput(html));
  const [counterPlugin] = useState(createCounterPlugin());
  const [counterOverLimit, setCounterOverLimit] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setEditorState(transformHtmlInput(html));
    }, 0);
  }, [html]);
  useEffect(() => {
    sendHtml();
  });
  const editorRef = useRef(); // Handle key commands to change text styling
  // Ex. Ctrl + B for "bold"

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      setEditorState(newState);
      return "handled";
    }

    return "not-handled";
  };

  const toggleInlineStyle = (e, style) => {
    e.preventDefault();
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (e, blockType) => {
    e.preventDefault();
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  }; // Set CSS class on the given block's type


  const textEditorBlockStyle = contentBlock => {
    const type = contentBlock.getType();

    if (type === blockTypes.h1.blockName) {
      return blockTypes.h1.class;
    } else {
      return null;
    }
  };

  const inlineIsActive = (state, style) => {
    return state.getCurrentInlineStyle().has(style);
  };

  const blockIsActive = (state, blockName) => {
    return blockName === state.getCurrentContent().getBlockForKey(state.getSelection().getStartKey()).getType();
  };

  const getCurrentEntityKey = editorState => {
    const selection = editorState.getSelection();
    const anchorKey = selection.getAnchorKey();
    const contentState = editorState.getCurrentContent();
    const anchorBlock = contentState.getBlockForKey(anchorKey);
    const offset = selection.anchorOffset;
    const index = selection.isBackward ? offset - 1 : offset;
    return anchorBlock.getEntityAt(index);
  };

  const getCurrentEntity = (editorState, entityKey) => {
    const contentState = editorState.getCurrentContent();

    if (entityKey) {
      return contentState.getEntity(entityKey);
    }

    return null;
  };

  const removeLink = editorState => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const startKey = selectionState.getStartKey();
    const contentBlock = contentState.getBlockForKey(startKey);
    const startOffset = selectionState.getStartOffset();
    const entity = contentBlock.getEntityAt(startOffset);

    if (!entity) {
      return editorState;
    }

    let entitySelection = null;
    contentBlock.findEntityRanges(character => character.getEntity() === entity, (start, end) => {
      entitySelection = selectionState.merge({
        anchorOffset: start,
        focusOffset: end
      });
    });
    setEditorState(RichUtils.toggleLink(editorState, entitySelection, null));
  }; // Get user input for link and create draft.js link entity


  const onEditLink = event => {
    const selection = editorState.getSelection();
    const start = selection.getStartOffset();
    const end = selection.getEndOffset();
    const currentEntityKey = getCurrentEntityKey(editorState);
    const entity = getCurrentEntity(editorState, currentEntityKey);
    let currentLink = "";

    if (entity && entity.getType() === "LINK") {
      currentLink = entity.getData().url;
    }

    if (start === end && !currentLink) {
      event.preventDefault();
      return;
    }

    const link = window.prompt(addLinkPrompt, currentLink);

    if (link === null) {
      return "handled";
    }

    if (!link) {
      if (currentLink) {
        removeLink(editorState);
      }

      return "handled";
    }

    const content = editorState.getCurrentContent();

    if (entity) {
      content.mergeEntityData(currentEntityKey, {
        url: link
      });
      const updatedEditorState = EditorState.push(editorState, content);
      setEditorState(updatedEditorState);
      return "handled";
    }

    const contentWithEntity = content.createEntity("LINK", "MUTABLE", {
      url: link
    });
    const newEditorState = EditorState.push(editorState, contentWithEntity, "create-entity");
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    setEditorState(RichUtils.toggleLink(newEditorState, selection, entityKey));
  };

  const specialTools = [{
    name: "link",
    click: onEditLink,
    label: React.createElement(LinkIcon, {
      style: {
        display: "block",
        margin: "auto"
      }
    })
  }];

  const countFunction = () => {
    const decodeUnicode = str => punycode.ucs2.decode(str); // func to handle unicode characters


    const plainText = editorState.getCurrentContent().getPlainText("");
    const regex = /(?:\r\n|\r|\n)/g; // new line, carriage return, line feed

    const cleanString = plainText.replace(regex, "").trim(); // replace above characters w/ nothing

    const count = decodeUnicode(cleanString).length;

    if (count > characterCountLimit) {
      setCounterOverLimit(true);
    } else {
      setCounterOverLimit(false);
    }

    return count;
  };

  const editorIsEmpty = () => {
    return !editorState.getCurrentContent().hasText();
  };

  const save = () => {
    let hasError = false;

    if (counterOverLimit) {
      hasError = true;
    }

    if (editorIsEmpty()) {
      hasError = true;
    }

    if (hasError) {
      setError(true);
      return;
    }
  };

  const clearError = () => {
    setError(false);
  };

  return React.createElement("div", null, React.createElement(TextEditorBox, {
    onClick: () => {
      editorRef.current.focus();
      clearError();
    },
    warning: counterOverLimit,
    error: error
  }, React.createElement(Editor, {
    blockStyleFn: textEditorBlockStyle,
    editorState: editorState,
    handleKeyCommand: handleKeyCommand,
    onChange: setEditorState,
    plugins: [counterPlugin, linkPlugin],
    stripPastedStyles: true,
    ref: editorRef
  })), React.createElement(TextEditorToolbar, {
    click: clearError,
    inlineStyles: inlineStyles,
    blockTypes: Object.values(blockTypes),
    specialTools: specialTools,
    inlineStyleActive: inlineIsActive,
    inlineOnMouseDown: toggleInlineStyle,
    blockIsActive: blockIsActive,
    blockOnMouseDown: toggleBlockType,
    characterCountLimit: characterCountLimit,
    editorState: editorState,
    counterPlugin: counterPlugin,
    countFunction: countFunction
  }));
};